import React, { useMemo, useEffect, useState, lazy } from 'react';
import CollapsibleSection from '../../common/CollapsibleSection.jsx';
import isEqual from 'lodash/isEqual';
import { createDefaults } from '../../../helpers/schema.js';
import styles from './ThemeConfiguration.module.scss';
import withSuspense from '../../../helpers/withSuspense.jsx';
import InputSkeleton from '../../../skeleton_loaders/common/InputSkeleton.jsx';

const CommonColorPicker = withSuspense(lazy(() => import('../../common/ColorPicker/CommonColorPicker.jsx')), InputSkeleton);
const CommonSlider = withSuspense(lazy(() => import('../../common/CommonSlider.jsx')), InputSkeleton);

const SETTINGS_MAP = {
  color: CommonColorPicker,
  number: CommonSlider,
};

const groupBy = (arr) =>
  arr.reduce((acc, f) => {
    (acc[f.group] ||= []).push(f);
    return acc;
  }, {});

const EMPTY_ARR = [];

function ThemeConfiguration({ title, themeVars: initialThemeVars, schema }) {
  const themeVars = initialThemeVars ?? {};
  const [themeFields, setThemeFields] = useState(null);

  useEffect(() => {
    let mounted = true;
    import('./theme.js').then((m) => {
      if (mounted) setThemeFields(m.default);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const grouped = useMemo(() => groupBy(themeFields ?? EMPTY_ARR), [themeFields]);

  const defaults = useMemo(() => {
    return createDefaults(schema, [schema]);
  }, [schema]);

  const [vars, setVars] = useState(() => ({ ...defaults, ...themeVars }));

  useEffect(() => {
    const next = { ...defaults, ...themeVars };
    setVars((prev) => (isEqual(prev, next) ? prev : next));
  }, [defaults, themeVars]);

  const setVar = (key, value) => {
    setVars((prev) => ({ ...prev, [key]: value }));
  };

  if (!themeFields) return null;

  return (
    <CollapsibleSection title={title} initialCollapsed={true} collapseHeadingSize={2}>
      {Object.entries(grouped).map(([group, fields]) => (
        <div key={group}>
          <CollapsibleSection title={I18n.t(group, {scope: 'theme_vars_translations'})} initialCollapsed={true} collapseHeadingSize={4}>
            <div className={styles['settings-group']}>
              {fields.map((f) => {
                const Component = SETTINGS_MAP[f.type];
                if (!Component) return null;

                return (
                  <div key={f.key} className={styles['setting']}>
                    <label>{I18n.t(f.labelKey, {scope: 'theme_vars_translations'})}</label>
                    <Component
                      value={vars[f.key]}
                      onChange={(v) => setVar(f.key, v)}
                      name={`settings[theme_vars][${f.key}]`}
                    />
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        </div>
      ))}
    </CollapsibleSection>
  );
}

export default ThemeConfiguration;
